/**
 * The matter's whole path, for staff: the same map the client sees in their tracker,
 * with the firm's own stage names, and the one-click suggestion that follows the work.
 *
 * Moving one step at a time is `TransitionDialog`'s ordinary list. Two deliberate
 * exceptions live here, each with a reason the server insists on:
 *   - SKIP AHEAD to a later step (lawyers and administrators). The steps in between show
 *     as done in the client's tracker.
 *   - CORRECT BACK to an earlier step (administrators and the responsible lawyer). The
 *     client's tracker updates quietly; they are not told their case went backwards.
 */

import {
  ConflictError,
  STAFF,
  qk,
  useApi,
  useApiMutation,
  useApiQuery,
  type CaseDetail,
  type StageOverview,
  type StagePathStep,
} from '@legalcrm/api';
import { Badge, Button, Card, CardBody, cn, useToast } from '@legalcrm/ui';

export function useStageOverview(caseId: string, enabled = true) {
  return useApiQuery<StageOverview>(
    qk.sub('cases', caseId, 'stage-overview'),
    STAFF.caseStageOverview(caseId),
    { enabled },
  );
}

/** What picking a step on the path would do, for this person. */
export function pathAction(
  index: number,
  path: StagePathStep[],
  overview: StageOverview,
): 'skip' | 'correct' | null {
  const at = path.findIndex((s) => s.state === 'current');
  if (at < 0) return null;
  if (index > at + 1 && overview.can_skip) return 'skip';
  if (index < at && overview.can_correct) return 'correct';
  return null;
}

export function StagePathList({
  overview,
  selected,
  onPick,
}: {
  overview: StageOverview;
  selected: string | null;
  onPick: (key: string, action: 'skip' | 'correct') => void;
}) {
  const { path } = overview;
  return (
    <ol className="flex flex-col" aria-label="The matter's path">
      {path.map((step, index) => {
        const action = pathAction(index, path, overview);
        const isSelected = selected === step.key;
        return (
          <li key={step.key} className="relative flex gap-3 pb-2 last:pb-0">
            {index < path.length - 1 && (
              <span
                className={cn(
                  'absolute left-[0.6875rem] top-6 h-[calc(100%-1rem)] w-0.5',
                  step.state === 'done' ? 'bg-accent' : 'bg-line',
                )}
                aria-hidden="true"
              />
            )}
            <span
              className={cn(
                'relative z-10 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-[0.7rem] font-bold',
                step.state === 'done' && 'border-accent bg-accent text-white',
                step.state === 'current' &&
                  'border-accent bg-surface text-accent ring-4 ring-accent/15',
                step.state === 'upcoming' && 'border-line bg-surface text-ink-subtle',
              )}
              aria-hidden="true"
            >
              {step.state === 'done' ? '✓' : index + 1}
            </span>
            <span className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2">
              <span className="min-w-0">
                <span
                  className={cn(
                    'block text-sm',
                    step.state === 'current' ? 'font-semibold text-ink' : 'text-ink-muted',
                  )}
                >
                  {step.name}
                  {step.state === 'current' && (
                    <Badge tone="info" size="sm" className="ml-2">
                      {overview.paused ? 'Paused here' : 'Now'}
                    </Badge>
                  )}
                </span>
                {step.client_title && step.client_title !== step.name && (
                  <span className="block text-xs text-ink-subtle">
                    Client sees: {step.client_title}
                  </span>
                )}
              </span>
              {action && (
                <Button
                  size="sm"
                  variant={isSelected ? 'primary' : 'ghost'}
                  onClick={() => onPick(step.key, action)}
                  aria-pressed={isSelected}
                >
                  {action === 'skip' ? 'Skip to here' : 'Move back here'}
                </Button>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/** "Documents have been requested — move to Documents needed?" with one click. */
export function StageSuggestionBanner({ caseRecord }: { caseRecord: CaseDetail }) {
  const api = useApi();
  const { toast } = useToast();
  const canMove = caseRecord._actions?.transition ?? false;
  const overview = useStageOverview(caseRecord.id, canMove);
  const suggestion = overview.data?.suggestion ?? null;

  const accept = useApiMutation<CaseDetail, void>(
    {
      mutationFn: () =>
        suggestion!.skips
          ? api.post<CaseDetail>(`${STAFF.caseMoveStage(caseRecord.id)}/`, {
              to_stage: suggestion!.to_stage,
              reason: suggestion!.reason,
            })
          : api.post<CaseDetail>(STAFF.caseTransition(caseRecord.id), {
              to_stage: suggestion!.to_stage,
              reason: '',
            }),
      invalidates: [qk.all('cases'), qk.all('case-timeline')],
    },
    {
      onSuccess: (updated) =>
        toast({
          title: 'Stage changed',
          description: `${caseRecord.case_number} is now at ${updated.current_stage.name}. The client has been told.`,
          tone: 'success',
        }),
      onError: (error) =>
        toast({
          title: 'The move was refused',
          description:
            error instanceof ConflictError ? error.detail : 'The stage could not be changed.',
          tone: 'warning',
        }),
    },
  );

  if (!canMove || !suggestion) return null;
  return (
    <Card className="mb-4 border-accent/40">
      <CardBody className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-ink">
            Ready to move to {suggestion.to_stage_name}?
          </p>
          <p className="text-sm text-ink-muted">
            {suggestion.reason} The client’s progress tracker will update.
          </p>
        </div>
        <Button variant="primary" loading={accept.isPending} onClick={() => accept.mutate()}>
          Move to {suggestion.to_stage_name}
        </Button>
      </CardBody>
    </Card>
  );
}
